import {ActionIcon, AppShell, Box, Burger, Card, MantineProvider, Menu} from '@mantine/core'
import {Notifications} from '@mantine/notifications'
import {IconSettings} from '@tabler/icons-react'
import {useEffect, useState} from 'react'
import {Link, Navigate, Route, Routes, useLocation, useNavigate} from 'react-router-dom'
import {AuthMenuItem} from '../components/menuItems/auth/AuthMenuItem.tsx'
import {ColorSchemeSwitchButton} from '../components/colorSchemeSwitch/ColorSchemeSwitchButton.tsx'
import {SubscriptionMenuItem} from '../components/menuItems/subscription/SubscriptionMenuItem.tsx'
import {UserAvatar} from '../components/userAvatar/UserAvatar.tsx'
import {useLocalSettings} from '../providers/localSettings/useLocalSettings.ts'
import {routes} from '../routes.ts'
import {headerHeight, mobileBreakpoint, theme} from '../theme.tsx'
import {CalendarScreen} from './calendar/CalendarScreen.tsx'
import {SubscriptionsScreen} from './stripe/SubscriptionsScreen.tsx'
import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/charts/styles.css'
import {ProjectsProvider} from '../providers/projects/ProjectsProvider.tsx'
import {ConnectionsScreen} from './connections/ConnectionsScreen.tsx'
import {ProjectsScreen} from './projects/ProjectsScreen.tsx'
import {withAuthenticationRequired} from '@auth0/auth0-react'
import {DashboardScreen} from './dashboard/DashboardScreen.tsx'
import {UserSettingsProvider} from '../providers/userSettings/UserSettingsProvider.tsx'
import {UserSettingsScreen} from './userSettings/UserSettingsScreen.tsx'
import {NavigationItems} from './NavigationItems.tsx'
import {SubscriptionProvider} from '../providers/subscription/SubscriptionProvider.tsx'
import {ConnectionsProvider} from '../providers/connections/ConnectionsProvider.tsx'
import {ErrorBanners} from './errorBanner/ErrorBanners.tsx'
import {Logo} from '../components/logo/Logo.tsx'

export const Layout = withAuthenticationRequired(() => {
  const [navbarOpen, setNavbarOpen] = useState<boolean>(false)
  const {colorScheme} = useLocalSettings()
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => location && setNavbarOpen(false), [location])

  useEffect(() => {
    document.title = 'devtime_'
  })

  return (
    <MantineProvider
      theme={theme}
      defaultColorScheme={'auto'}
      forceColorScheme={colorScheme !== 'auto' ? colorScheme : undefined}
    >
      <UserSettingsProvider>
        <SubscriptionProvider>
          <ProjectsProvider>
            <ConnectionsProvider>
              <Notifications />
              <AppShell
                header={{
                  height: headerHeight,
                }}
                navbar={{
                  width: 0,
                  breakpoint: mobileBreakpoint,
                  collapsed: {
                    mobile: !navbarOpen,
                  },
                }}
              >
                <AppShell.Header>
                  <Card radius={0} h={headerHeight}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        height: '100%',
                      }}
                    >
                      <div style={{display: 'flex', gap: 20}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 20}}>
                          <Burger
                            opened={navbarOpen}
                            onMouseDown={() => setNavbarOpen(!navbarOpen)}
                            hiddenFrom='md'
                            size='sm'
                          />
                          <Box onMouseDown={() => navigate(routes.home.buildRoute())} style={{cursor: 'pointer'}}>
                            <Logo animate={false} />
                          </Box>
                        </div>

                        <Box style={{display: 'flex', gap: 15}} visibleFrom={mobileBreakpoint}>
                          <NavigationItems />
                        </Box>
                      </div>

                      <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                        <Menu withArrow>
                          <Menu.Target>
                            <ActionIcon variant={'subtle'} size={'lg'} radius='xl'>
                              <UserAvatar />
                            </ActionIcon>
                          </Menu.Target>

                          <Menu.Dropdown>
                            <SubscriptionMenuItem />
                            <Menu.Item
                              leftSection={<IconSettings stroke={1} />}
                              component={Link}
                              to={routes.settings.root.buildRoute()}
                            >
                              Settings
                            </Menu.Item>
                            <Menu.Label pb={0}>Color scheme</Menu.Label>
                            <Menu.Label>
                              <ColorSchemeSwitchButton />
                            </Menu.Label>
                            <AuthMenuItem />
                          </Menu.Dropdown>
                        </Menu>
                      </div>
                    </div>
                  </Card>
                </AppShell.Header>

                <AppShell.Navbar w={'100%'} hiddenFrom={mobileBreakpoint}>
                  <NavigationItems />
                </AppShell.Navbar>

                <AppShell.Main>
                  <Box p={'md'} style={{margin: 'auto'}}>
                    <ErrorBanners />
                    <Routes>
                      <Route index element={<Navigate to={routes.dashboard.buildRoute()} replace />} />
                      <Route path={routes.dashboard.route} element={<DashboardScreen />} />
                      <Route path={routes.calendar.route} element={<CalendarScreen />} />
                      <Route path={routes.projects.route} element={<ProjectsScreen />} />
                      <Route path={routes.connections.route} element={<ConnectionsScreen />} />
                      <Route path={routes.subscriptions.root.route} element={<SubscriptionsScreen />} />
                      <Route path={routes.settings.root.route} element={<UserSettingsScreen />} />
                    </Routes>
                  </Box>
                </AppShell.Main>
              </AppShell>
            </ConnectionsProvider>
          </ProjectsProvider>
        </SubscriptionProvider>
      </UserSettingsProvider>
    </MantineProvider>
  )
})
